import appConfig from '@frontend/env';
import { http } from '@frontend/fetch';
import { flattenWithDots } from '@frontend/uri';
import {
  PaymentPlanUpdateFields,
  PaymentPlanDetailsResponse,
  PaymentPlanDetailsHistoryResponse,
  CreatePlanData,
  PersonPaymentMethodsResponse,
  PaymentMethodResponse,
  PaymentPlanSearchParams,
  PaymentPlanListResponse,
  ClientSecretLinkResponse,
  ClientSecretResponse,
  PaymentMethod,
  CreatePlanResponse,
  PaymentPlanUpdateReviewResponse,
  PaymentPlanStatus,
  PlanConfirmationRequest,
  PlanConfirmationResponse,
  SendConfirmationEmailRequest,
  PaymentPlanConfirmationStatus,
  CreatePMRegistrationLinkRequest,
  CancelPlanResponse,
} from './types';

const { PAY_BACKEND_API } = appConfig;

const LOCATION_ID_HEADER = 'Location-Id';
const addLocationHeader = (locationId: string, headers: Record<string, string> = {}) => ({
  headers: { [LOCATION_ID_HEADER]: locationId, ...headers },
});

export const getPaymentPlanList = async (filter: PaymentPlanSearchParams) =>
  await http.getData<PaymentPlanListResponse>(`payments/v2/paymentplans`, { params: flattenWithDots(filter) });

export const createPaymentPlan = async (planDetails: CreatePlanData) => {
  const { locationId, ...rest } = planDetails;
  return await http
    .post<CreatePlanResponse>(
      `payments/v2/paymentplans`,
      { ...rest },
      { ...addLocationHeader(locationId, { 'Content-Type': 'application/json' }) }
    );
};
export const getPaymentPlanDetails = async (paymentPlanId: string) =>
  await http.get<PaymentPlanDetailsResponse>(`payments/v2/paymentplans/${paymentPlanId}`);

export const getPaymentPlanOptions = async () => await http.get(`payments/v2/paymentplans/options`);

export const getPaymentPlanHistory = async (paymentPlanId: string) =>
  await http.get<PaymentPlanDetailsHistoryResponse>(`payments/v2/paymentplans/${paymentPlanId}/history`);

export const deletePaymentPlan = async (paymentPlanId: string) =>
  await http.delete(`payments/v2/paymentplans/${paymentPlanId}`, {
    headers: { 'Content-Type': 'application/json' },
  });

export const cancelPaymentPlan = async (paymentPlanId: string) =>
  await http.patch<CancelPlanResponse>(
    `payments/v2/paymentplans/${paymentPlanId}`,
    { status: PaymentPlanStatus.Canceled },
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );

export const reviewUpdatePaymentPlan = async (paymentPlanId: string, updates: PaymentPlanUpdateFields) =>
  await http.patch<PaymentPlanUpdateReviewResponse>(
    `payments/v2/paymentplans/${paymentPlanId}/review`,
    { ...updates },
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );

export const updatePaymentPlan = async (paymentPlanId: string, updates: PaymentPlanUpdateFields) =>
  await http.patch<PlanConfirmationResponse>(
    `payments/v2/paymentplans/${paymentPlanId}`,
    { ...updates },
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );

export const pausePaymentPlan = async (paymentPlanId: string, reason: string) =>
  await http.patch<PlanConfirmationResponse>(
    `payments/v2/paymentplans/${paymentPlanId}`,
    {
      status: PaymentPlanStatus.Paused,
      confirmationStatus: PaymentPlanConfirmationStatus.PausedByOffice,
      notes: reason,
    },
    { headers: { 'Content-Type': 'application/json' } }
  );

export const resumePaymentPlan = async (paymentPlanId: string, resumeDate: string) =>
  await http.patch<PlanConfirmationResponse>(
    `payments/v2/paymentplans/${paymentPlanId}`,
    { status: PaymentPlanStatus.Active, startAt: resumeDate },
    { headers: { 'Content-Type': 'application/json' }, responseType: 'none' }
  );

// PAYMENT METHOD APIS

// Gets all payment methods sorted by person id for a location
export const getLocationPaymentMethods = async (locationId: string) => {
  const { personPaymentMethods, links } = await http.getData<PersonPaymentMethodsResponse>(
    `payments/locations/${locationId}/paymentMethods`,
    addLocationHeader(locationId)
  );
  return { personPaymentMethods, links };
};

// Gets details on person payment method id
export const getPersonPaymentMethod = async (paymentMethodId: string, locationId: string) => {
  const { paymentMethod } = await http.getData<PaymentMethodResponse>(
    `payments/paymentMethods/${paymentMethodId}`,
    addLocationHeader(locationId)
  );
  return paymentMethod;
};

export const deletePaymentMethod = async ({
  paymentMethodId,
  locationId,
}: {
  paymentMethodId: string;
  locationId: string;
}) => await http.delete(`payments/paymentMethods/${paymentMethodId}`, null, addLocationHeader(locationId));

// Gets all payment methods associated with a person
export const getPersonPaymentMethods = async (patientId: string, locationId: string) =>
  http.get<PaymentMethod[]>(`payments/person/${patientId}/paymentMethods`, addLocationHeader(locationId));

// Sets default payment method with id
export const setDefaultPaymentMethod = async (paymentMethodId: string, locationId: string) => {
  const paymentSet = await http.patch(
    `payments/paymentMethods/${paymentMethodId}/default`,
    addLocationHeader(locationId, { 'Content-Type': 'application/json' })
  );
  return paymentSet;
};

// CLIENT SECRET APIS FOR STRIPE

export const getClientSecretLink = async (
  setupLink: string | undefined,
  data: { data: { personId?: string; locationId: string; paymentMethodId?: string } }
) => {
  if (setupLink) {
    const response = await http.post<ClientSecretLinkResponse>(setupLink, data, {
      ...addLocationHeader(data.data.locationId, { 'Content-Type': 'application/json' }),
      responseType: 'none',
    });
    return response.headers.get('location');
  }
  return;
};

export const getClientSecret = async (secretLink: string | undefined, locationId: string) => {
  if (secretLink) {
    const response = await http.getData<ClientSecretResponse>(
      secretLink,
      addLocationHeader(locationId, { 'Content-Type': 'application/json' })
    );
    return response;
  }
  return;
};

export const getPaymentPlanConfirmationDetails = async (planId: string) =>
  http.get<PaymentPlanDetailsResponse>(`${PAY_BACKEND_API}/payments/v2/${planId}/confirm`, {
    headers: { 'Content-Type': 'application/json' },
    skipValidation: true,
  });

export const confirmPaymentPlan = (planConfirmationRequest: PlanConfirmationRequest) =>
  http.patch<PlanConfirmationResponse, PlanConfirmationRequest>(
    `${PAY_BACKEND_API || ''}/payments/v2/confirm`,
    planConfirmationRequest,
    {
      headers: { 'Content-Type': 'application/json' },
      skipValidation: true,
    }
  );

export const sendConfirmationEmail = (sendConfirmationEmailRequest: SendConfirmationEmailRequest) =>
  http.post<unknown, SendConfirmationEmailRequest>(`payments/v2/paymentplans/authorize`, sendConfirmationEmailRequest, {
    headers: { 'Content-Type': 'application/json' },
  });

export const createPaymentRegistrationLink = ({ personId, email, locationId }: CreatePMRegistrationLinkRequest) =>
  http.post<unknown, CreatePMRegistrationLinkRequest>(
    `payments/commands/service/person/${personId}/paymentMethods/registration:link`,
    { email, personId, locationId },
    addLocationHeader(locationId)
  );
